<template>
    <!--
        The design's profile: personal details and company details side by side, each a
        two-column grid, no title band. The five password rules are always on show; the
        fields, checks and save are the ones the form had.
    -->
    <div class="profile">
        <div class="profile-col">
            <p class="member-heading">{{ $lang.Member.MemberInfo }}</p>

            <div class="member-grid">
                <b-field :label="$lang.Member.Name" :type="{ 'is-danger': hasError_memberName }" :message="emptyWarning_memberName">
                    <b-input v-model="memberName" :maxlength="limits.firstName"></b-input>
                </b-field>

                <b-field :label="$lang.Member.Surname" :type="{ 'is-danger': hasError_memberSurname }" :message="emptyWarning_memberSurname">
                    <b-input v-model="memberSurname" :maxlength="limits.lastName"></b-input>
                </b-field>

                <b-field class="is-span-two" :label="$lang.Member.Email" :type="{ 'is-danger': hasError_email }" :message="emptyWarning_email">
                    <b-input v-model="email" type="text" :maxlength="limits.emailAddress"></b-input>
                </b-field>

                <div class="is-span-two">
                    <b-field :label="$lang.Member.Password" :type="{ 'is-danger': hasError_password }" :message="emptyWarning_password">
                        <password-input v-model="password" autocomplete="new-password" @input="password_check"></password-input>
                    </b-field>
                    <!-- The fields open empty; the rules show once a new password is typed. -->
                    <p v-if="!password" class="help">{{ $lang.Member.PasswordKeepHint }}</p>
                    <div v-else class="auth-rules profile-rules">
                        <p class="frmValidation" :class="{'frmValidation--passed' : password.length > 7}"><i class="frmIcon mdi" :class="password.length > 7 ? 'mdi-check-circle' : 'mdi-close-circle'"></i> {{ $lang.Password.Password_1 }}</p>
                        <p class="frmValidation" :class="{'frmValidation--passed' : has_uppercase }"><i class="frmIcon mdi" :class="has_uppercase ? 'mdi-check-circle' : 'mdi-close-circle'"></i> {{ $lang.Password.Password_2 }}</p>
                        <p class="frmValidation" :class="{'frmValidation--passed' : has_lowercase }"><i class="frmIcon mdi" :class="has_lowercase ? 'mdi-check-circle' : 'mdi-close-circle'"></i> {{ $lang.Password.Password_3 }}</p>
                        <p class="frmValidation" :class="{'frmValidation--passed' : has_number }"><i class="frmIcon mdi" :class="has_number ? 'mdi-check-circle' : 'mdi-close-circle'"></i> {{ $lang.Password.Password_4 }}</p>
                        <p class="frmValidation" :class="{'frmValidation--passed' : has_special }"><i class="frmIcon mdi" :class="has_special ? 'mdi-check-circle' : 'mdi-close-circle'"></i> {{ $lang.Password.Password_5 }}</p>
                    </div>
                </div>

                <b-field class="is-span-two" :label="$lang.Member.Password_2" :type="{ 'is-danger': hasError_password_2 }" :message="emptyWarning_password_2">
                    <password-input v-model="password_2" autocomplete="new-password"></password-input>
                </b-field>
            </div>
        </div>

        <!-- Company. Same field set as the registration form. -->
        <div class="profile-col">
            <p class="member-heading">{{ $lang.Member.CompanyInfo }}</p>

            <div class="member-grid">
                <b-field class="is-span-two" :label="$lang.Member.Title" :type="{ 'is-danger': hasError_title }" :message="emptyWarning_title">
                    <b-input v-model="title" :maxlength="limits.tradeName"></b-input>
                </b-field>

                <b-field :label="$lang.Member.Country" :type="{ 'is-danger': hasError_country }" :message="emptyWarning_country">
                    <b-select v-model="country" expanded @input="onCountryChanged()">
                        <option v-for="(option, index) in countryOptions" :value="option.name" :key="index">
                            {{ countryLabel(option) }}
                        </option>
                    </b-select>
                </b-field>

                <!-- The CRM requires a city only when the country is Türkiye. -->
                <b-field v-if="isCityRequired" :label="$lang.Member.City" :type="{ 'is-danger': hasError_city }" :message="emptyWarning_city">
                    <b-select v-model="city" expanded @input="setCityId()">
                        <option v-for="option in cityNameList" :value="option" :key="option">
                            {{ option }}
                        </option>
                    </b-select>
                </b-field>

                <b-field class="is-span-two" :label="$lang.Member.Phone">
                    <phone-input ref="phoneInput"
                                 v-model="phone"
                                 :locale="isTr ? 'tr' : 'en'"
                                 required />
                </b-field>
            </div>

            <div class="profile-actions">
                <b-button class="profile-save" type="is-primary" :loading="isSaving" @click="submit()">{{ $lang.Member.UpdateBtn }}</b-button>
                <span class="profile-note">{{ $lang.Member.ProfileNote }}</span>
            </div>
        </div>
    </div>
</template>

<script>
    import router from '@/router';
    import api from '@/api';
    import PasswordInput from '@/components/PasswordInput.vue';
    import PhoneInput from '@/components/PhoneInput.vue';
    import {
        COMM_LANGUAGE_TR_ID,
        COMM_LANGUAGE_EN_ID,
        COMM_LANGUAGE_TR_NAME,
        COMM_LANGUAGE_EN_NAME,
        COUNTRY_TURKIYE_NAME,
        isValidEmail,
        LIMITS
    } from '@/constants/registration';

    export default {
        name: 'UpdateMember',
        components: { PasswordInput, PhoneInput },
        data() {
            return {
                isSaveBtnClicked: false,
                isSaving: false,

                memberName: '',
                memberSurname: '',
                email: '',
                password: '',
                password_2: '',
                has_number: false,
                has_lowercase: false,
                has_uppercase: false,
                has_special: false,

                title: '',
                country: '',
                countryId: '',
                countryList: [],
                city: '',
                cityId: '',
                cityList: [],
                cityNameList: [],
                phone: '',

                langList: [],
                langNameList: [],
                langId: '',
                commLanguage: '',

                limits: LIMITS,

                hasError_memberName: false,
                emptyWarning_memberName: '',
                hasError_memberSurname: false,
                emptyWarning_memberSurname: '',
                hasError_email: false,
                emptyWarning_email: '',
                hasError_password: false,
                emptyWarning_password: '',
                hasError_password_2: false,
                emptyWarning_password_2: '',
                hasError_title: false,
                emptyWarning_title: '',
                hasError_country: false,
                emptyWarning_country: '',
                hasError_city: false,
                emptyWarning_city: ''
            };
        },
        computed: {
            isTr() {
                return this.$lang.Format.Lang == 'tr-TR';
            },

            isCityRequired() {
                return this.country == COUNTRY_TURKIYE_NAME;
            },

            /**
             * The English form lists countries by their English names, but the option value
             * stays the Turkish name - that is what Members.Country holds, so a member who
             * registers in English still finds their country selected when they later open
             * the Turkish profile.
             *
             * Sorted by whatever is displayed: the list arrives in Turkish order, which
             * would scatter the English labels.
             */
            countryOptions() {
                var self = this;
                return this.countryList.slice().sort(function (a, b) {
                    return self.countryLabel(a).localeCompare(
                        self.countryLabel(b), self.isTr ? 'tr' : 'en');
                });
            },
        },
        created() {
            this.$store.state.isFooterFixed = true;
            this.getMember();
        },
        destroyed() {
            this.$store.state.isFooterFixed = false;
        },
        methods: {
            /* ---------- load ---------- */

            getMember() {
                api.members.getMyProfile().then((res) => {
                    if (res == null || res.isError || res.data == null) {
                        return;
                    }

                    var member = res.data;
                    this.memberName = member.Name;
                    this.memberSurname = member.SurName;
                    this.email = member.Email;
                    // The API no longer sends the password; the fields stay empty unless the
                    // member types a new one.
                    this.title = member.Title;
                    this.country = member.Country;
                    this.city = member.City;

                    // Stored in E.164, so it goes straight into the flag control. The old
                    // code did substring(4) here, which truncated non-Turkish numbers.
                    this.phone = member.Phone || '';

                    this.commLanguage = this.isTr ? COMM_LANGUAGE_TR_NAME : COMM_LANGUAGE_EN_NAME;
                    this.langId = this.isTr ? COMM_LANGUAGE_TR_ID : COMM_LANGUAGE_EN_ID;

                    this.getLangList();
                    this.getCountryList();
                });
            },

            getCountryList() {
                this.countryList = [];
                api.members.getCountries().then((res) => {
                    if (res == null || res.isError) {
                        return;
                    }
                    this.countryList = res.data;
                    this.resolveCountryId();

                    // The flag used to be inferred from the stored number alone, so a member
                    // whose country has no dial code - Kuzey Kıbrıs, Abkhazia - opened their
                    // profile looking at some unrelated country's flag. Telling the control
                    // which country it is also puts it into the plain box when there is no
                    // dial code to show. getMember has already set this.country by here.
                    this.$nextTick(this.syncPhoneCountry);

                    if (this.isCityRequired) {
                        this.getCityList();
                    }
                });
            },

            resolveCountryId() {
                var found = this.findCountry(this.country);
                this.countryId = found ? found.id : '';
            },

            findCountry(name) {
                for (var i = 0; i < this.countryList.length; i++) {
                    if (this.countryList[i].name === name) {
                        return this.countryList[i];
                    }
                }
                return null;
            },

            /** Falls back to the Turkish name for a country the CRM has no English name for. */
            countryLabel(option) {
                return (!this.isTr && option.englishName) ? option.englishName : option.name;
            },


            onCountryChanged() {
                this.resolveCountryId();
                this.city = '';
                this.cityId = '';
                this.cityNameList = [];
                this.cityList = [];
                if (this.isCityRequired) {
                    this.getCityList();
                }
                this.syncPhoneCountry();
            },

            /**
             * The dial code follows the country the visitor picked. Asked for by the
             * customer: choosing Germany and then being offered a +1 placeholder reads as
             * the form ignoring the answer it was just given.
             *
             * Digits already typed are kept. A country with no dial code - the CRM returns
             * a few, such as Antarctica - leaves the field untouched.
             */
            syncPhoneCountry() {
                var option = this.findCountry(this.country);
                if (option && option.sapCode && this.$refs.phoneInput) {
                    this.$refs.phoneInput.setCountry(option.sapCode);
                }
            },

            getCityList() {
                this.cityNameList = [];
                this.cityList = [];
                if (!this.countryId) {
                    return;
                }
                api.members.getCities(this.countryId).then((res) => {
                    if (res == null || res.isError) {
                        return;
                    }
                    res.data.forEach((e) => {
                        this.cityNameList.push(e.name);
                        this.cityList.push(e);
                    });
                    this.setCityId();
                });
            },

            setCityId() {
                if (this.city != '' && this.city != null) {
                    var i = this.cityNameList.indexOf(this.city);
                    this.cityId = i === -1 ? '' : this.cityList[i].id;
                }
            },

            getLangList() {
                api.members.getCommLanguages().then((res) => {
                    if (res == null || res.isError) {
                        return; // keep the constant fallback set in getMember
                    }
                    this.langNameList = [];
                    this.langList = [];
                    res.data.forEach((e) => {
                        this.langNameList.push(e.name);
                        this.langList.push(e);
                    });
                    var i = this.langNameList.indexOf(this.commLanguage);
                    if (i !== -1) {
                        this.langId = this.langList[i].id;
                    }
                });
            },

            /* ---------- validation ---------- */

            password_check() {
                this.has_number = /\d/.test(this.password);
                this.has_lowercase = /[a-z]/.test(this.password);
                this.has_uppercase = /[A-Z]/.test(this.password);
                this.has_special = /[!@#$%^&*()+=._-]/.test(this.password);
            },

            clearErrors() {
                this.hasError_memberName = false; this.emptyWarning_memberName = '';
                this.hasError_memberSurname = false; this.emptyWarning_memberSurname = '';
                this.hasError_email = false; this.emptyWarning_email = '';
                this.hasError_password = false; this.emptyWarning_password = '';
                this.hasError_password_2 = false; this.emptyWarning_password_2 = '';
                this.hasError_title = false; this.emptyWarning_title = '';
                this.hasError_country = false; this.emptyWarning_country = '';
                this.hasError_city = false; this.emptyWarning_city = '';
            },

            fail(field, message) {
                this['hasError_' + field] = true;
                this['emptyWarning_' + field] = message;
            },

            validate() {
                this.clearErrors();
                var empty = this.$lang.Menus.EmptyWarning;
                var ok = true;

                if (this.memberName.trim() == '') { this.fail('memberName', empty); ok = false; }
                if (this.memberSurname.trim() == '') { this.fail('memberSurname', empty); ok = false; }

                if (this.email.trim() == '') {
                    this.fail('email', empty); ok = false;
                } else if (!isValidEmail(this.email.trim())) {
                    this.fail('email', this.$lang.Member.EmailError); ok = false;
                }

                // Both empty keeps the current password; a new one must pass the rules and be repeated.
                if (this.password != '' || this.password_2 != '') {
                    if (!this.isPasswordStrong()) {
                        this.fail('password', empty); ok = false;
                    }
                    if (this.password_2 == '' || this.password_2 != this.password) {
                        this.fail('password_2', empty); ok = false;
                    }
                }

                if (this.title.trim() == '') { this.fail('title', empty); ok = false; }

                if (this.country == '' || this.countryId == '') {
                    this.fail('country', empty); ok = false;
                }
                if (this.isCityRequired && (this.city == '' || this.cityId == '')) {
                    this.fail('city', empty); ok = false;
                }

                if (!this.validatePhone()) { ok = false; }

                return ok;
            },

            isPasswordStrong() {
                return this.password.length > 7 &&
                    this.has_number && this.has_lowercase && this.has_uppercase && this.has_special;
            },

            validatePhone() {
                var input = this.$refs.phoneInput;
                return input ? input.validate() : false;
            },

            /* ---------- submit ---------- */

            submit() {
                this.isSaveBtnClicked = true;
                if (this.isSaving || !this.validate()) {
                    return;
                }

                // Town and PhoneBusiness are not collected by this form and
                // MemberManager.UpdateMember no longer writes those columns, so whatever was
                // captured at registration survives. They are still sent, empty, because
                // MemberModel declares them as non-nullable strings and model binding
                // rejects the whole request without them - AddMember.vue does the same.
                // AllowComm is a bool, so binding defaults it and it needs no placeholder.
                var member = {
                    Id: this.$store.state.memberId,
                    Name: this.memberName.trim(),
                    SurName: this.memberSurname.trim(),
                    Email: this.email.trim(),
                    Passwordd: this.password,
                    Title: this.title.trim(),
                    Country: this.country,
                    CountryId: this.countryId,
                    City: this.city,
                    CityId: this.cityId,
                    Town: '',
                    Phone: this.phone,
                    PhoneBusiness: '',
                    CommLanguage: this.commLanguage,
                    CommLangId: this.langId
                };

                this.isSaving = true;
                api.members.encryptMember(member).then((res) => {
                    if (res == null || res.isError) {
                        this.isSaving = false;
                        return;
                    }
                    this.checkUniqueThenSave(res.data);
                });
            },

            checkUniqueThenSave(encrypted) {
                var memberId = this.$store.state.memberId;

                api.members.IsMailUnique_ForUpdate(memberId, encrypted.Email).then((mailRes) => {
                    if (mailRes == null || mailRes.isError) { this.isSaving = false; return; }
                    if (!mailRes.data) {
                        this.isSaving = false;
                        this.fail('email', this.$lang.Member.EmailError);
                        return;
                    }

                    api.members.IsPhoneNumberUnique_ForUpdate(memberId, encrypted.Phone).then((phoneRes) => {
                        if (phoneRes == null || phoneRes.isError) { this.isSaving = false; return; }
                        if (!phoneRes.data) {
                            this.isSaving = false;
                            this.$buefy.dialog.alert({
                                title: this.$lang.Member.ErrorTitle,
                                message: this.$lang.Member.MobilePhone,
                                confirmText: this.$lang.Member.ErrorClose
                            });
                            return;
                        }

                        this.save(encrypted);
                    });
                });
            },

            save(encrypted) {
                // Id is not encrypted; carry it over explicitly.
                encrypted.Id = this.$store.state.memberId;

                api.members.updateMember(encrypted).then((res) => {
                    this.isSaving = false;
                    if (res == null || res.isError) {
                        if (res && res.isError) this.$buefy.dialog.alert(this.$errorModal(res));
                        return;
                    }

                    // A mutation, so the persisted copy is written too.
                    this.$store.commit('user', Object.assign({}, this.$store.state.userInfo, { Name: this.memberName.trim() }));
                    this.$buefy.toast.open(this.$lang.Member.MemberUpdated);
                    router.push('/');
                });
            }
        }
    };
</script>

<style lang="scss">
    /* Personal | company, as in the design. The field grid is scss/_member-form.scss. */
    .profile {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        align-items: start;
        gap: 40px;
        /* The page width, centred above it (scss/_page-width.scss). */
        width: 100%;
        max-width: var(--page-max);
        margin: 0 auto;
        padding: 40px;
    }

    .profile-col {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 0;
    }

    /* The rules panel is #f7f7f7 on the new-password card, which is white; here it sits on
       the #f7f7f7 page, so it takes white and a hairline to stay visible. */
    .auth-rules.profile-rules {
        margin-top: 10px;
        background: #ffffff;
        border: 1px solid #e3e3e3;
    }

    .profile-actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 14px;
        padding-top: 4px;
    }

    .button.profile-save {
        height: 50px;
        padding: 0 28px;
        font-size: 16px;
    }

    .profile-note {
        font-size: 13px;
        font-weight: 300;
        color: #6b6b6b;
    }

    @media screen and (max-width: 768px) {
        .profile {
            grid-template-columns: minmax(0, 1fr);
            gap: 30px;
            padding: 26px 18px 36px;
        }
    }
</style>
